import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-ext-400.css";
import { canUseWebGL } from "@/lib/motion";

export const SKIP_TARGET = "icerik-baslangic";
export const STRIPES = ["#FFDC1A", "#F78F1E", "#00AEEF", "#D71920"];

export const skipToContent = (e) => {
  e.preventDefault();
  const el = document.getElementById(SKIP_TARGET);
  if (!el) return;
  const header = document.querySelector("[data-testid=site-header]")?.offsetHeight || 0;
  if (window.__avterLenis) window.__avterLenis.scrollTo(el, { offset: -header, immediate: true, force: true });
  else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - header, behavior: "auto" });
  requestAnimationFrame(() => requestAnimationFrame(() => el.focus({ preventScroll: true })));
};

export const SkipLink = () => (
  <a href={`#${SKIP_TARGET}`} onClick={skipToContent} data-testid="hero-skip-link"
    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors duration-300 hover:border-white/60 hover:text-white">
    Atla <ArrowDown className="h-4 w-4" aria-hidden="true" />
  </a>
);

// Big masked typography (real HTML). Copy comes from the CMS (home hero heading + site footer note).
export const HeroType = ({ title, subtitle, animate, typeRef }) => {
  const words = title.split(" ");
  const lines = words.length > 1 ? [words.slice(0, -1).join(" "), words[words.length - 1]] : [title];
  return (
    <div ref={typeRef} className={`font-hero relative z-10 ${animate ? "hero-animate" : ""}`} data-testid="hero-type">
      <p className="text-[12vw] font-semibold leading-[0.9] tracking-[-0.04em] text-white md:text-[7.6vw]">
        {lines.map((l, i) => <span key={l} className="mask"><span style={{ animationDelay: `${0.15 + i * 0.12}s` }}>{l}</span></span>)}
      </p>
      <p className="font-hero-text mt-6 text-base text-white/65 md:mt-8 md:text-xl">
        <span className="mask"><span style={{ animationDelay: "0.5s" }}>{subtitle}</span></span>
      </p>
    </div>
  );
};

const LightStripes = ({ animate }) => (
  <div className={`light-stripes ${animate ? "is-animated" : ""}`} aria-hidden="true">
    {STRIPES.map((c, i) => <span key={c} style={{ "--c": c, "--h": `${46 + i * 12}%`, animationDelay: `${0.4 + i * 0.12}s` }} />)}
  </div>
);

// Mobile + motion: four light trails stream in once and settle into the logo stripes, then drift slightly on scroll.
const LightFlow = () => {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = Math.min(window.scrollY, window.innerHeight);
        if (ref.current) ref.current.style.transform = `translate3d(0, ${(y * -0.06).toFixed(1)}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={ref} className="light-flow" aria-hidden="true" data-testid="hero-light-flow">
      {STRIPES.map((c, i) => (
        <span key={c} className="lf-lane" style={{ "--c": c, "--h": `${46 + i * 12}%`, "--d": `${0.2 + i * 0.14}s`, "--x": `${-62 - i * 10}vw`, "--y": `${34 - i * 4}vh` }}>
          <span className="lf-trail" />
        </span>
      ))}
    </div>
  );
};

export const Poster = ({ hidden }) => (
  <img src="/hero-frames/poster.webp" alt="" aria-hidden="true" width="1440" height="792" decoding="async"
    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${hidden ? "opacity-0" : "opacity-100"}`}
    onError={(e) => { e.currentTarget.style.display = "none"; }} />
);

// Desktop + motion: same layout as the 3D scene, poster stands in until the chunk is loaded.
const PosterHero = ({ title, subtitle }) => (
  <section className="relative h-[calc(100vh-108px)] min-h-[520px] overflow-hidden bg-[#05070D]" data-testid="hero-cinematic-poster">
    <Poster />
    <div className="container-site relative flex h-full flex-col justify-end pb-16 pt-24">
      <HeroType title={title} subtitle={subtitle} animate />
    </div>
    <div className="absolute bottom-6 right-6 z-20 md:right-10"><SkipLink /></div>
  </section>
);

const StaticHero = ({ title, subtitle, animate, tall }) => (
  <section className={`relative overflow-hidden bg-[#05070D] ${tall ? "h-[calc(100vh-108px)] min-h-[520px]" : "min-h-[78vh]"}`} data-testid={`hero-cinematic-static${animate ? "" : "-reduced"}`}>
    {animate && !tall ? <LightFlow /> : <LightStripes animate={animate} />}
    <div className="container-site relative flex h-full min-h-[inherit] flex-col justify-end pb-16 pt-24">
      <HeroType title={title} subtitle={subtitle} animate={animate} />
      <div className="mt-10"><SkipLink /></div>
    </div>
  </section>
);

// 3D scene only on desktop + motion + WebGL; the chunk is imported only in that case.
export const HeroIntro = ({ reduced, narrow, title, subtitle }) => {
  const [Scene, setScene] = useState(null);
  const [webgl] = useState(() => !reduced && !narrow && canUseWebGL());
  useEffect(() => {
    if (!webgl) return undefined;
    let cancelled = false;
    let id;
    const events = ["pointermove", "pointerdown", "wheel", "touchstart", "keydown", "scroll"];
    // 3D chunk loads on first user intent; the poster shows the identical opening frame until then.
    const run = () => {
      events.forEach((e) => window.removeEventListener(e, run));
      id = (window.requestIdleCallback || ((cb) => setTimeout(cb, 50)))(() => {
        if (cancelled) return;
        import(/* webpackChunkName: "hero-3d" */ "@/components/home/HeroScene3D").then((m) => !cancelled && setScene(() => m.default));
      }, { timeout: 400 });
    };
    events.forEach((e) => window.addEventListener(e, run, { once: true, passive: true }));
    return () => { cancelled = true; events.forEach((e) => window.removeEventListener(e, run)); (window.cancelIdleCallback || clearTimeout)(id); };
  }, [webgl]);
  if (Scene) return <Scene title={title} subtitle={subtitle} />;
  if (!reduced && !narrow && webgl) return <PosterHero title={title} subtitle={subtitle} />;
  return <StaticHero title={title} subtitle={subtitle} animate={!reduced} tall={!narrow} />;
};
